// ─── Prompt Assistant Instructions ───────────────────────────────────
// Этот файл экспортирует PromptInstructionsPanel, который подключается в AiLabPage.
// Вынесен отдельно чтобы не раздувать AiLabPage.tsx.

import { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, ArrowUp, ArrowDown, Eye, EyeOff } from 'lucide-react';
import { siteProxy } from '../../services/siteProxy';

interface PromptInstructionAdmin {
  id: string;
  label: string;
  description?: string;
  systemPrompt: string;
  extractMode: 'bracket-prompt' | 'full';
  order: number;
  isBuiltin?: boolean;
  hidden?: boolean;
}

export function PromptInstructionsPanel() {
  const [items, setItems] = useState<PromptInstructionAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<PromptInstructionAdmin | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState({
    id: '',
    label: '',
    description: '',
    systemPrompt: '',
    extractMode: 'bracket-prompt' as 'bracket-prompt' | 'full',
    hidden: false,
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('ai', '/api/prompt-instructions')) as PromptInstructionAdmin[];
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setIsNew(true);
    setEditing(null);
    setForm({ id: '', label: '', description: '', systemPrompt: '', extractMode: 'bracket-prompt', hidden: false });
  };

  const startEdit = (p: PromptInstructionAdmin) => {
    setIsNew(false);
    setEditing(p);
    setForm({
      id: p.id,
      label: p.label,
      description: p.description ?? '',
      systemPrompt: p.systemPrompt,
      extractMode: p.extractMode,
      hidden: !!p.hidden,
    });
  };

  const cancel = () => {
    setEditing(null);
    setIsNew(false);
    setForm({ id: '', label: '', description: '', systemPrompt: '', extractMode: 'bracket-prompt', hidden: false });
  };

  const save = async () => {
    if (!form.label.trim() || !form.systemPrompt.trim()) return;
    setSaving(true);
    try {
      if (isNew) {
        await siteProxy('ai', '/api/prompt-instructions', {
          method: 'POST',
          body: {
            id: form.id.trim() || undefined,
            label: form.label.trim(),
            description: form.description.trim(),
            systemPrompt: form.systemPrompt,
            extractMode: form.extractMode,
            hidden: form.hidden,
          },
        });
      } else if (editing) {
        await siteProxy('ai', `/api/prompt-instructions/${encodeURIComponent(editing.id)}`, {
          method: 'PATCH',
          body: {
            label: form.label.trim(),
            description: form.description.trim(),
            systemPrompt: form.systemPrompt,
            extractMode: form.extractMode,
            hidden: form.hidden,
          },
        });
      }
      cancel();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    }
    setSaving(false);
  };

  const remove = async (p: PromptInstructionAdmin) => {
    if (p.isBuiltin) return;
    if (!confirm(`Удалить инструкцию "${p.label}"?`)) return;
    try {
      await siteProxy('ai', `/api/prompt-instructions/${encodeURIComponent(p.id)}`, {
        method: 'DELETE',
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось удалить');
    }
  };

  const moveItem = async (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setItems(reordered);
    try {
      await siteProxy('ai', '/api/prompt-instructions/reorder', {
        method: 'POST',
        body: { ids: reordered.map((x) => x.id) },
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось изменить порядок');
      await load();
    }
  };

  const toggleHidden = async (p: PromptInstructionAdmin) => {
    try {
      await siteProxy('ai', `/api/prompt-instructions/${encodeURIComponent(p.id)}`, {
        method: 'PATCH',
        body: { hidden: !p.hidden },
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось переключить видимость');
    }
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;

  return (
    <div className="pt-4 space-y-4">
      {error && (
        <div className="p-2 rounded-lg text-xs bg-red-500/10 border border-red-500/20 text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-500 font-medium">Порядок в селекторе ассистента</p>
            <button
              onClick={startNew}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-indigo-600 hover:bg-indigo-500 text-white transition"
            >
              <Plus className="w-3 h-3" />
              Новая
            </button>
          </div>
          {items.length === 1 && (
            <p className="text-[10px] text-slate-600 mb-2">
              Стрелки ↑/↓ активируются, когда появится 2+ инструкции.
            </p>
          )}
          {items.length === 0 ? (
            <p className="text-sm text-slate-600 py-4 text-center">Нет инструкций</p>
          ) : (
            items.map((p, idx) => (
              <div
                key={p.id}
                className={`flex items-center gap-2 p-3 rounded-lg border transition ${
                  editing?.id === p.id
                    ? 'bg-indigo-500/10 border-indigo-500/30'
                    : 'bg-slate-900/60 border-white/[0.06]'
                }`}
              >
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveItem(idx, -1)}
                    disabled={idx === 0}
                    className="p-0.5 rounded text-slate-500 hover:text-indigo-400 transition disabled:opacity-20 disabled:cursor-not-allowed"
                    title="Выше"
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => moveItem(idx, 1)}
                    disabled={idx === items.length - 1}
                    className="p-0.5 rounded text-slate-500 hover:text-indigo-400 transition disabled:opacity-20 disabled:cursor-not-allowed"
                    title="Ниже"
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium truncate ${p.hidden ? 'text-slate-500' : 'text-slate-200'}`}>{p.label}</p>
                    {p.isBuiltin && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                        built-in
                      </span>
                    )}
                    {p.hidden && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-400 border border-slate-600/30">
                        скрыто
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 font-mono truncate">{p.id}</p>
                  {p.description && (
                    <p className="text-xs text-slate-500 truncate mt-0.5">{p.description}</p>
                  )}
                </div>
                <button
                  onClick={() => toggleHidden(p)}
                  className="p-1.5 rounded text-slate-500 hover:text-indigo-400 transition"
                  title={p.hidden ? 'Показать всем' : 'Скрыть от обычных юзеров (видно только админам)'}
                >
                  {p.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={() => startEdit(p)}
                  className="p-1.5 rounded text-slate-500 hover:text-indigo-400 transition"
                  title="Редактировать"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => remove(p)}
                  disabled={p.isBuiltin}
                  className="p-1.5 rounded text-slate-500 hover:text-red-400 transition disabled:opacity-20 disabled:cursor-not-allowed"
                  title={p.isBuiltin ? 'Встроенные нельзя удалить' : 'Удалить'}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}
        </div>

        {editing || isNew ? (
          <div className="p-4 rounded-lg bg-slate-900/60 border border-white/[0.06] space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500 font-medium">
                {isNew ? 'Новая инструкция' : `Редактирование: ${editing?.label}`}
              </p>
              <button
                onClick={cancel}
                className="p-1 rounded text-slate-500 hover:text-slate-300 transition"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {isNew && (
              <div>
                <label className="text-[11px] text-slate-500 mb-1 block">
                  ID (slug, a-z0-9_-). Пусто — сгенерируется из названия.
                </label>
                <input
                  type="text"
                  value={form.id}
                  onChange={(e) => setForm({ ...form, id: e.target.value })}
                  className="input"
                  placeholder="my-instruction"
                />
              </div>
            )}

            <div>
              <label className="text-[11px] text-slate-500 mb-1 block">Название (в селекторе)</label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="input"
                placeholder="Принт на кружку"
              />
            </div>

            <div>
              <label className="text-[11px] text-slate-500 mb-1 block">Описание (опционально)</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="input"
                placeholder="Короткое пояснение для админа"
              />
            </div>

            <div>
              <label className="text-[11px] text-slate-500 mb-1 block">Режим извлечения промпта</label>
              <select
                value={form.extractMode}
                onChange={(e) =>
                  setForm({ ...form, extractMode: e.target.value as 'bracket-prompt' | 'full' })
                }
                className="input"
              >
                <option value="bracket-prompt">[PROMPT] секция (рекомендуется)</option>
                <option value="full">Весь ответ целиком</option>
              </select>
              <p className="text-[10px] text-slate-600 mt-1">
                bracket-prompt — берёт содержимое [PROMPT] из ответа. full — использует ответ целиком.
              </p>
            </div>

            <div>
              <label className="text-[11px] text-slate-500 mb-1 block">
                System prompt (инструкция Grok)
              </label>
              <textarea
                value={form.systemPrompt}
                onChange={(e) => setForm({ ...form, systemPrompt: e.target.value })}
                className="input min-h-[240px] resize-y font-mono text-xs"
                placeholder="You are a senior prompt engineer..."
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.hidden}
                onChange={(e) => setForm({ ...form, hidden: e.target.checked })}
                className="w-4 h-4 accent-indigo-500"
              />
              <span className="text-xs text-slate-300">
                Скрыть от обычных юзеров (видно только админам)
              </span>
            </label>

            <div className="flex gap-2">
              <button
                onClick={save}
                disabled={saving || !form.label.trim() || !form.systemPrompt.trim()}
                className="px-3 py-1.5 rounded-lg text-xs bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-50"
              >
                {saving ? 'Сохранение...' : isNew ? 'Создать' : 'Обновить'}
              </button>
              <button
                onClick={cancel}
                className="px-3 py-1.5 rounded-lg text-xs bg-slate-800 text-slate-400 hover:bg-slate-700 transition"
              >
                Отмена
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 rounded-lg bg-slate-900/40 border border-dashed border-white/[0.06] flex items-center justify-center">
            <p className="text-xs text-slate-600 text-center">
              Выбери инструкцию для редактирования или нажми «Новая»
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
